import React, { ReactNode } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, useNavigate } from 'react-router-dom'
import { Auth0Provider as Auth0ProviderSDK } from '@auth0/auth0-react'
import { Auth0Provider } from './auth/auth0-provider'
import { RedemptionGate } from './auth/RedemptionGate'
import { AppCacheProvider } from './contexts/AppCacheContext'
import { ToastProvider } from './contexts/ToastContext'
import { ConfirmProvider } from './contexts/ConfirmContext'
import { Toaster } from './components/ui/toaster'
import { getAuth0ProviderOptions } from './auth/auth0-config'
import App from './App.tsx'
import './index.css'
// Block library styles are loaded app-wide so Puck/Render pick them up
// regardless of which feature route mounts first.
import '@puckeditor/core/puck.css'
import '@nailthelanding/blocks/tokens.css'
import '@nailthelanding/blocks/style.css'

const rootElement = document.getElementById('root')

if (!rootElement) {
  throw new Error('Failed to find the root element')
}

// Get Auth0 provider options - returns null if using mock mode (dev builds
// only; a misconfigured production build throws here instead of silently
// degrading to mock auth).
const auth0Options = getAuth0ProviderOptions();

// Post-auth restore targets must be internal app paths — never absolute or
// protocol-relative URLs (open-redirect guard for the appState round-trip).
const sanitizeReturnTo = (value: unknown): string => {
  if (typeof value === 'string' && value.startsWith('/') && !value.startsWith('//')) {
    return value;
  }
  return '/';
};

/**
 * Mounts the Auth0 SDK provider inside the router so onRedirectCallback can
 * restore the pre-login location via router navigation. The previous
 * history.replaceState approach mutated the URL without notifying
 * BrowserRouter, so deep links were immediately clobbered by the default
 * route redirect.
 */
const Auth0ProviderWithNavigate: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();

  if (!auth0Options) {
    // Mock mode — no Auth0 SDK needed
    return <>{children}</>;
  }

  return (
    <Auth0ProviderSDK
      domain={auth0Options.domain}
      clientId={auth0Options.clientId}
      authorizationParams={auth0Options.authorizationParams}
      cacheLocation={auth0Options.cacheLocation}
      useRefreshTokens={auth0Options.useRefreshTokens}
      onRedirectCallback={(appState) => {
        const state = appState as { returnTo?: string; inviteToken?: string } | undefined;
        if (state?.inviteToken) {
          sessionStorage.setItem('pendingInviteToken', state.inviteToken);
        }
        navigate(sanitizeReturnTo(state?.returnTo), { replace: true });
      }}
    >
      {children}
    </Auth0ProviderSDK>
  );
};

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <BrowserRouter>
      <Auth0ProviderWithNavigate>
        <Auth0Provider>
          {/* ToastProvider is above the gate so redemption feedback can toast.
              RedemptionGate holds back AppCacheProvider (and its /api/auth/me)
              until any pending invite redemption settles — see the gate's
              docstring for the race it closes. */}
          <ToastProvider>
            <RedemptionGate>
              <AppCacheProvider>
                <ConfirmProvider>
                  <App />
                </ConfirmProvider>
              </AppCacheProvider>
            </RedemptionGate>
            <Toaster />
          </ToastProvider>
        </Auth0Provider>
      </Auth0ProviderWithNavigate>
    </BrowserRouter>
  </React.StrictMode>,
)
